import { useEffect, useState, type ChangeEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { MAX_THEMES, publishTheme, saveTheme, THEME_NAME, type SavedTheme } from "../../api/themes";
import { customScheme } from "../../domain/schemes";
import {
  base64ToBytes, bytesToBase64, copyOverrides, copyProblems, isHexColour, MAX_THEME_FILE_BYTES, MIN_CARD_OPACITY, parseThemeFile,
  sniffLogo, themeFileName, toThemeFile, type ThemeContent, type ThemeImage,
} from "../../domain/themeFile";
import type { PortalLayout, PortalLogoSize } from "../../portal/api";
import { COPY_ENTRIES, copyBytes, LEGACY_COPY_KEYS, MAX_COPY_BYTES, normaliseCopyValue, type CopyEntry, type CopyGroup } from "../../portal/copy";
import type { PreviewScreen } from "../../portal/preview";
import { readBackground } from "./background";
import { describeError } from "./errors";
import { FRAME_SIZES, PortalFrame } from "./PortalFrame";
import { useOperatorDocuments } from "./useOperatorDocuments";
import { usePortalContact } from "./usePortalContact";

/**
 * The theme editor: colours, page background and layout, every text on the
 * guest pages, and the real portal page beside them, repainted on every change.
 * Opened on a variation, a saved theme, or an uploaded theme file; it downloads
 * the theme as a file and loads one back. Only Save writes anything.
 *
 * Design: radius-wiki portal-brand-from-logo.md, "Portal text per theme" and
 * "Page background, layout, contact and legal pages".
 */

export interface EditorStart extends ThemeContent {
  /** The saved theme's logo on the server, when the editor was opened on a saved theme. */
  storedLogo: (() => Promise<Blob>) | null;
  /** The saved theme's background picture on the server, when it has one. */
  storedBackground: (() => Promise<Blob>) | null;
  /** Keys in an uploaded file that this console does not know. */
  unknown: string[];
}

const TABS: Array<{ id: "colours" | CopyGroup; label: string; screen: PreviewScreen }> = [
  { id: "colours", label: "Colours & background", screen: "login" },
  { id: "page", label: "Heading", screen: "login" },
  { id: "login", label: "Sign in", screen: "login" },
  { id: "register", label: "Sign up", screen: "register" },
  { id: "forgot", label: "Forgot password", screen: "forgot" },
  { id: "myplan", label: "My plan", screen: "myplan" },
  { id: "recharge", label: "Recharge", screen: "recharge" },
  { id: "payment", label: "Payment", screen: "payment" },
  { id: "online", label: "Online", screen: "connected" },
  { id: "legal", label: "Legal & footer", screen: "legal" },
  { id: "messages", label: "Messages", screen: "register" },
];

const COLOUR_FIELDS: Array<{ field: "ground" | "ink" | "accent"; label: string; help: string }> = [
  { field: "ground", label: "Main colour", help: "60% — the base of the page." },
  { field: "ink", label: "Text and header", help: "30% — text, and the header band." },
  { field: "accent", label: "Buttons", help: "10% — buttons; links use a readable shade of it." },
];

const LAYOUTS: Array<{ value: PortalLayout; label: string; help: string }> = [
  { value: "compact", label: "Compact card", help: "On a computer: one card, 960 px wide, as today." },
  { value: "wide", label: "Wide card", help: "On a computer: a larger card, 1200 px wide." },
  { value: "full", label: "Full screen", help: "On a computer: no card — your picture fills the left half, the form the right." },
];

// Owner 2026-10-30: a logo looked small whatever file was uploaded, because the
// page caps how large it draws it. medium is the size before this choice.
const LOGO_SIZES: Array<{ value: PortalLogoSize; label: string }> = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "xlarge", label: "Extra large" },
];

export function ThemeEditor({ start, ns, themes, onClose }: { start: EditorStart | null; ns: string; themes: SavedTheme[]; onClose: () => void }) {
  return (
    <Dialog open={start !== null} onClose={onClose} fullScreen>
      {start && <EditorBody start={start} ns={ns} themes={themes} onClose={onClose} />}
    </Dialog>
  );
}

/** An object URL for the image being edited, or the stored one; revoked when it changes. */
function useImageUrl(image: ThemeImage | null, stored: (() => Promise<Blob>) | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let made: string | null = null;
    const show = (b: Blob) => {
      if (!live) return;
      made = URL.createObjectURL(b);
      setUrl(made);
    };
    const bytes = image ? base64ToBytes(image.base64) : null;
    if (image && bytes) show(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: image.type }));
    else if (stored) stored().then(show, () => live && setUrl(null));
    else setUrl(null);
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [image, stored]);
  return url;
}

/** The image to send: the edited one, else the stored bytes (to copy them into a theme saved under a new name). */
async function imageToSend(image: ThemeImage | null, stored: (() => Promise<Blob>) | null): Promise<ThemeImage | null> {
  if (image) return image;
  if (!stored) return null;
  const raw = new Uint8Array(await (await stored()).arrayBuffer());
  const type = sniffLogo(raw);
  return type ? { type, base64: bytesToBase64(raw) } : null;
}

function EditorBody({ start, ns, themes, onClose }: { start: EditorStart; ns: string; themes: SavedTheme[]; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [content, setContent] = useState<ThemeContent>(() => ({
    name: start.name, ground: start.ground, ink: start.ink, accent: start.accent,
    backdrop: start.backdrop, layout: start.layout, cardOpacity: start.cardOpacity, logoSize: start.logoSize,
    copy: { ...start.copy }, logo: start.logo, background: start.background,
  }));
  const [storedLogo, setStoredLogo] = useState<(() => Promise<Blob>) | null>(() => start.storedLogo);
  const [storedBackground, setStoredBackground] = useState<(() => Promise<Blob>) | null>(() => start.storedBackground);
  // Set once the operator removes the picture: the save then sends "" so the
  // stored one is deleted, rather than leaving the field out, which keeps it.
  const [backgroundRemoved, setBackgroundRemoved] = useState(false);
  const [backgroundReading, setBackgroundReading] = useState(false);
  const [backgroundProblem, setBackgroundProblem] = useState<string | null>(null);
  const [unknown, setUnknown] = useState<string[]>(start.unknown);
  const [fileProblems, setFileProblems] = useState<string[]>([]);
  const [tab, setTab] = useState(0);
  const [device, setDevice] = useState<"phone" | "desktop">("phone");
  const logoUrl = useImageUrl(content.logo, storedLogo);
  const backgroundUrl = useImageUrl(content.background, storedBackground);
  const contact = usePortalContact(ns);
  const documents = useOperatorDocuments(ns);

  const current = TABS[tab] ?? TABS[0]!;
  const coloursValid = isHexColour(content.ground) && isHexColour(content.ink) && isHexColour(content.accent);
  const backdropValid = content.backdrop === "" || isHexColour(content.backdrop);
  const hasBackground = Boolean(content.background || storedBackground);
  const scheme = coloursValid ? customScheme(content.ground, content.ink, content.accent) : null;
  const overrides = copyOverrides(content.copy);
  const problems = copyProblems(content.copy);
  const problemCount = Object.keys(problems).length;
  const bytes = copyBytes(overrides);
  const nameValid = THEME_NAME.test(content.name);
  const existing = themes.find((t) => t.name === content.name);
  const atLimit = !existing && themes.length >= MAX_THEMES;
  const canSave = coloursValid && backdropValid && nameValid && !atLimit && problemCount === 0 && bytes <= MAX_COPY_BYTES;
  const size = FRAME_SIZES[device];

  const save = useMutation({
    mutationFn: async () => {
      const renamed = content.name !== start.name;
      const logo = content.logo ?? (renamed ? await imageToSend(null, storedLogo) : null);
      const background = content.background
        ? content.background.base64
        : backgroundRemoved
          ? ""
          : renamed && storedBackground
            ? (await imageToSend(null, storedBackground))?.base64
            : undefined;
      return (await saveTheme(identity, ns, content.name, {
        ground: content.ground.toUpperCase(), ink: content.ink.toUpperCase(), accent: content.accent.toUpperCase(),
        backdrop: content.backdrop.toUpperCase(), layout: content.layout, cardOpacity: content.cardOpacity,
        logoSize: content.logoSize,
        copy: overrides,
        ...(logo ? { logo: logo.base64 } : {}),
        ...(background !== undefined ? { background } : {}),
      })).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portalThemes", ns] }),
  });
  const publish = useMutation({
    mutationFn: async (name: string) => (await publishTheme(identity, ns, name)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portalThemes", ns] }),
  });

  const edit = (patch: Partial<ThemeContent>) => {
    save.reset();
    setContent((c) => ({ ...c, ...patch }));
  };
  const setText = (key: string, value: string) => {
    save.reset();
    setContent((c) => {
      const copy = { ...c.copy };
      if (value === "") delete copy[key];
      else copy[key] = value;
      return { ...c, copy };
    });
  };

  async function download() {
    const logo = await imageToSend(content.logo, storedLogo).catch(() => content.logo);
    const background = await imageToSend(content.background, storedBackground).catch(() => content.background);
    const url = URL.createObjectURL(new Blob([toThemeFile({ ...content, logo, background })], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = themeFileName(content.name);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_THEME_FILE_BYTES) {
      setFileProblems([`The file is ${Math.round(file.size / 1024)} KB; a theme file is at most about 2 MB.`]);
      return;
    }
    const r = parseThemeFile(await file.text());
    if (!r.ok) {
      setFileProblems(r.problems);
      return;
    }
    setFileProblems([]);
    save.reset();
    setUnknown(r.unknown);
    // A file without a logo or picture keeps the one already in the editor, and
    // a version 1 file (no page settings at all) keeps the editor's page.
    const page = r.version === 1
      ? { backdrop: content.backdrop, layout: content.layout, cardOpacity: content.cardOpacity, logoSize: content.logoSize, background: content.background }
      : { background: r.content.background ?? content.background };
    setContent({ ...r.content, ...page, name: r.content.name || content.name, logo: r.content.logo ?? content.logo });
    if (r.content.logo) setStoredLogo(null);
    if (r.version !== 1 && r.content.background) {
      setStoredBackground(null);
      setBackgroundRemoved(false);
    }
  }

  async function onBackgroundFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBackgroundProblem(null);
    setBackgroundReading(true);
    try {
      edit({ background: await readBackground(file) });
      setStoredBackground(null);
      setBackgroundRemoved(false);
    } catch (err) {
      setBackgroundProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBackgroundReading(false);
    }
  }

  function removeBackground() {
    edit({ background: null });
    setStoredBackground(null);
    setBackgroundRemoved(true);
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ p: 2, borderBottom: 1, borderColor: "divider", alignItems: { md: "flex-start" } }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Edit portal theme</Typography>
          <Typography variant="body2" color="text.secondary">Colours, background and every text on your guest pages. The page on the right is your real portal.</Typography>
        </Box>
        <TextField
          size="small"
          label="Theme name"
          value={content.name}
          onChange={(e) => edit({ name: e.target.value })}
          error={!nameValid}
          helperText={
            !nameValid ? "1-64 letters, digits, '-' or '_'"
              : existing?.published ? "This theme is live: saving changes your portal."
                : existing ? "Replaces the saved theme of this name."
                  : atLimit ? `You already have ${MAX_THEMES} saved themes.` : " "
          }
        />
        <Button variant="outlined" component="label">
          Upload theme file
          <input hidden type="file" accept="application/json,.json" onChange={(e) => void onFile(e)} />
        </Button>
        <Button variant="outlined" disabled={!coloursValid} onClick={() => void download()}>Download theme file</Button>
        <Button variant="contained" disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save theme"}
        </Button>
        <Button onClick={onClose}>Close</Button>
      </Stack>

      <Box sx={{ px: 2 }}>
        {fileProblems.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setFileProblems([])}>
            <AlertTitle>That file was not loaded</AlertTitle>
            {fileProblems.map((p) => <div key={p}>{p}</div>)}
          </Alert>
        )}
        {unknown.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }} onClose={() => setUnknown([])}>
            The file has texts this portal does not use, so they were left out: {unknown.join(", ")}.
          </Alert>
        )}
        {problemCount > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {problemCount === 1 ? "One text needs fixing" : `${problemCount} texts need fixing`} before saving:{" "}
            {Object.keys(problems).map((k) => COPY_ENTRIES.find((e) => e.key === k)?.label ?? k).join(", ")}.
          </Alert>
        )}
        {bytes > MAX_COPY_BYTES && (
          <Alert severity="warning" sx={{ mt: 2 }}>The texts add up to {bytes} bytes; the limit is {MAX_COPY_BYTES}. Shorten the longest ones.</Alert>
        )}
        {save.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>{describeError(save.error).title}</AlertTitle>
            {describeError(save.error).detail}
          </Alert>
        )}
        {save.isSuccess && (
          <Alert
            severity="success"
            sx={{ mt: 2 }}
            action={
              !save.data.published && (
                <Button color="inherit" size="small" disabled={publish.isPending || publish.isSuccess} onClick={() => publish.mutate(save.data.name)}>
                  {publish.isSuccess ? "Published" : "Publish now"}
                </Button>
              )
            }
          >
            Saved "{save.data.name}"{save.data.published ? " — it is live." : "."}
          </Alert>
        )}
        {publish.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>{describeError(publish.error).title}</AlertTitle>
            {describeError(publish.error).detail}
          </Alert>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(360px, 1fr) minmax(440px, 1.1fr)" }, overflow: { xs: "auto", md: "hidden" } }}>
        <Box sx={{ overflowY: { md: "auto" }, borderRight: { md: 1 }, borderColor: "divider" }}>
          <Tabs
            value={tab}
            onChange={(_, v: number) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider", position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper" }}
          >
            {TABS.map((t) => {
              const bad = t.id === "colours" ? !backdropValid : COPY_ENTRIES.some((e) => e.group === t.id && problems[e.key]);
              return <Tab key={t.id} label={bad ? `${t.label} ⚠` : t.label} />;
            })}
          </Tabs>
          <Box sx={{ p: 2 }}>
            {current.id === "colours" ? (
              <Stack spacing={2}>
                {COLOUR_FIELDS.map(({ field, label, help }) => (
                  <ColourField key={field} label={label} help={help} value={content[field]} onChange={(v) => edit({ [field]: v })} />
                ))}
                {scheme && (
                  <Box>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <Chip size="small" variant="outlined" label={`${scheme.mode} page`} />
                    </Stack>
                    <Typography variant="body2">
                      Text {scheme.ratios.text.toFixed(1)}:1 · Button {scheme.ratios.button.toFixed(1)}:1 · Link {scheme.ratios.link.toFixed(1)}:1
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Every text colour is adjusted, only as far as needed, to read at 4.5:1 or better.
                      {scheme.ratios.rawAccent < 1.4 ? " Your button colour is very close to the page colour, so buttons will be hard to see." : ""}
                    </Typography>
                  </Box>
                )}

                <Divider />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Background</Typography>

                <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                  <Box
                    role="img"
                    aria-label={backgroundUrl ? "Your background picture" : "No background picture"}
                    sx={{
                      width: 120, height: 80, flexShrink: 0, borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "action.hover",
                      backgroundImage: backgroundUrl ? `url("${backgroundUrl}")` : "none", backgroundSize: "cover", backgroundPosition: "center",
                    }}
                  />
                  <Stack spacing={1} sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" size="small" component="label" disabled={backgroundReading} sx={{ whiteSpace: "nowrap" }}>
                        {backgroundReading ? "Reading…" : hasBackground ? "Change picture" : "Choose picture"}
                        <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => void onBackgroundFile(e)} />
                      </Button>
                      {hasBackground && <Button size="small" color="error" onClick={removeBackground}>Remove</Button>}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      A full-size picture behind your portal. Your browser makes it smaller first: at most 1920 px and 600 KB.
                    </Typography>
                  </Stack>
                </Stack>
                {backgroundProblem && <Alert severity="error" onClose={() => setBackgroundProblem(null)}>{backgroundProblem}</Alert>}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={content.backdrop === ""}
                      onChange={(e) => edit({ backdrop: e.target.checked ? "" : content.ground.toUpperCase() })}
                    />
                  }
                  label="Page colour: same as the main colour"
                />
                {content.backdrop !== "" && (
                  <ColourField
                    label="Page colour"
                    help="Around the card on a computer, and under your picture while it loads."
                    value={content.backdrop}
                    onChange={(v) => edit({ backdrop: v })}
                  />
                )}

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Logo size</Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={content.logoSize}
                    onChange={(_, v: PortalLogoSize | null) => {
                      if (v) edit({ logoSize: v });
                    }}
                    aria-label="Logo size"
                  >
                    {LOGO_SIZES.map((s) => <ToggleButton key={s.value} value={s.value}>{s.label}</ToggleButton>)}
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    How large guests see your logo. It never grows wider than the screen. A bigger file alone does not make it larger.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Layout</Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={content.layout}
                    onChange={(_, v: PortalLayout | null) => {
                      if (!v) return;
                      edit({ layout: v });
                      // The layouts differ only on a computer, so show one.
                      setDevice("desktop");
                    }}
                  >
                    {LAYOUTS.map((l) => <ToggleButton key={l.value} value={l.value}>{l.label}</ToggleButton>)}
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {LAYOUTS.find((l) => l.value === content.layout)?.help}{" "}
                    On a phone the page is one card{hasBackground ? ", floating over your picture" : ""}.
                  </Typography>
                </Box>

                {hasBackground && (
                  <Box>
                    <Typography variant="body2">Card opacity: {content.cardOpacity}%</Typography>
                    <Slider
                      size="small"
                      min={MIN_CARD_OPACITY}
                      max={100}
                      step={1}
                      value={content.cardOpacity}
                      onChange={(_, v) => edit({ cardOpacity: Array.isArray(v) ? v[0]! : v })}
                      aria-label="Card opacity"
                      sx={{ maxWidth: 320 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Below 100% your picture shows faintly through the form. Text is checked against a solid card, so keep it high over a busy picture.
                    </Typography>
                  </Box>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                {current.id === "page" && (
                  <Typography variant="body2" color="text.secondary">
                    Left empty, the heading and subheading are the ones your hotspot already shows.
                  </Typography>
                )}
                {current.id === "legal" && (
                  <Typography variant="body2" color="text.secondary">
                    The footer links and the Legal &amp; Terms page. The documents themselves — pricing note, terms, privacy and refunds —
                    are written once under <b>Legal &amp; Terms</b> in the menu and shown with every theme. The preview shows sample text.
                  </Typography>
                )}
                {COPY_ENTRIES.filter((e) => e.group === current.id).map((e) => (
                  <CopyField key={e.key} entry={e} value={content.copy[e.key] ?? ""} problem={problems[e.key] ?? null} onChange={(v) => setText(e.key, v)} />
                ))}
              </Stack>
            )}
          </Box>
        </Box>

        <Box sx={{ p: 2, overflowY: { md: "auto" }, bgcolor: "action.hover" }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 2 }}>
            <ToggleButtonGroup size="small" exclusive value={device} onChange={(_, v: "phone" | "desktop" | null) => v && setDevice(v)}>
              <ToggleButton value="phone">Phone</ToggleButton>
              <ToggleButton value="desktop">Desktop</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">Sample data. Sign-up and recharge work on test numbers; the SMS code is 123456.</Typography>
          </Stack>
          {scheme ? (
            <PortalFrame
              theme={{
                ground: scheme.ground, ink: scheme.ink, accent: scheme.accent, logoUrl, copy: overrides,
                ...(backdropValid && content.backdrop ? { backdrop: content.backdrop } : {}),
                backgroundUrl, layout: content.layout, cardOpacity: content.cardOpacity, logoSize: content.logoSize, contact, documents,
              }}
              screen={current.screen}
              width={size.width}
              height={size.height}
            />
          ) : (
            <Alert severity="warning">Fix the colours to see your portal.</Alert>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function ColourField({ label, help, value, onChange }: { label: string; help: string; value: string; onChange: (v: string) => void }) {
  const ok = isHexColour(value);
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
      <Box
        component="input"
        type="color"
        aria-label={`${label} colour picker`}
        value={ok ? value.toLowerCase() : "#000000"}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value.toUpperCase())}
        sx={{ width: 48, height: 40, p: 0, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "transparent", cursor: "pointer" }}
      />
      <TextField
        size="small"
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value.trim())}
        error={!ok}
        helperText={ok ? help : "Write it as #RRGGBB"}
        sx={{ flex: 1 }}
      />
    </Stack>
  );
}

function CopyField({ entry, value, problem, onChange }: { entry: CopyEntry; value: string; problem: string | null; onChange: (v: string) => void }) {
  const set = normaliseCopyValue(value) !== "";
  const legacy = LEGACY_COPY_KEYS.includes(entry.key);
  const hint = [
    set ? `${[...normaliseCopyValue(value)].length}/${entry.max}` : legacy ? "Empty: your hotspot's own text is shown" : "Empty: the text shown in grey is used",
    entry.vars?.length ? `you can use ${entry.vars.map((v) => `{${v}}`).join(" ")}` : "",
  ].filter(Boolean).join(" · ");
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
      <TextField
        size="small"
        label={entry.label}
        value={value}
        placeholder={legacy ? "" : entry.default}
        onChange={(e) => onChange(e.target.value)}
        error={problem !== null}
        helperText={problem ?? hint}
        multiline={Boolean(entry.multiline) || entry.max > 160}
        {...(entry.multiline ? { minRows: 6 } : {})}
        // The label stays above the field, so an empty field shows the text guests
        // will see (the grey placeholder) instead of hiding it under the label.
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ flex: 1, minWidth: 0 }}
      />
      {set ? (
        <Button size="small" sx={{ whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => onChange("")}>Reset</Button>
      ) : legacy ? null : (
        <Button size="small" sx={{ whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => onChange(entry.default)}>Edit</Button>
      )}
    </Stack>
  );
}
